import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { DataTable, type DataFetchParams } from '@/components/DataTable';
import { TABLE_IDS } from '@/components/DataTable/tableIds';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { useToast } from '@/hooks/use-toast';
import { useDeleteSecurityGroup } from '@/hooks/api/useSecurityGroups';
import type { SecurityGroup } from '@kirin/api-client/contracts/security-groups';
import { SecurityGroupFormDialog } from './SecurityGroupFormDialog';
import { createSecurityGroupsColumns } from './columns';
import {
  fetchSecurityGroupsPage,
  SECURITY_GROUP_SORT_FIELDS,
} from './securityGroupsTable';

/**
 * Security Groups list (KIR-3800, AC #1, #2, #4, #18).
 *
 * Uses the shared DataTable so this page behaves like Alerts and Audit Logs
 * (sorting, pagination, persisted state). Clicking anywhere on a row opens the
 * group, which is where members are managed.
 */
export function SecurityGroupsList({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  /** Owners and managers only (AC #17); members get a read-only table. */
  canEdit: boolean;
}): ReactElement {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<SecurityGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<SecurityGroup | null>(
    null
  );
  const { toast } = useToast();
  const navigate = useNavigate();

  const deleteMutation = useDeleteSecurityGroup(organizationId);

  const columns = useMemo(
    () =>
      createSecurityGroupsColumns(
        organizationId,
        canEdit,
        setGroupToEdit,
        setGroupToDelete
      ),
    [organizationId, canEdit]
  );

  const queryFn = useCallback(
    (params: DataFetchParams) =>
      fetchSecurityGroupsPage(organizationId, params),
    [organizationId]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => [
      'securityGroups',
      'dataTable',
      {
        organizationId,
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
      },
    ],
    [organizationId]
  );

  const handleDelete = async (): Promise<void> => {
    if (!groupToDelete) return;
    try {
      await deleteMutation.mutateAsync(groupToDelete.id);
      toast({
        title: 'Success',
        description: `"${groupToDelete.name}" was deleted`,
        variant: 'success',
      });
      setGroupToDelete(null);
    } catch (err) {
      toast({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to delete the group',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {canEdit ? (
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create group
          </Button>
        ) : null}
      </div>

      <DataTable<SecurityGroup>
        id={TABLE_IDS.ORGANIZATION.SECURITY_GROUPS}
        mode="server"
        columns={columns}
        features={{ enableSorting: true }}
        initialState={{
          sort: {
            field: SECURITY_GROUP_SORT_FIELDS.NAME,
            order: SortOrder.Asc,
          },
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        onRowClick={group => navigate(group.id)}
        noDataLabel="No security groups yet. Create one to apply different policies to a set of users."
        errorBoundaryLabel="Something went wrong in the Security Groups table"
      />

      <SecurityGroupFormDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        organizationId={organizationId}
      />

      <SecurityGroupFormDialog
        isOpen={groupToEdit !== null}
        onClose={() => setGroupToEdit(null)}
        organizationId={organizationId}
        group={groupToEdit ?? undefined}
      />

      <ConfirmationDialog
        isOpen={groupToDelete !== null}
        onClose={() => setGroupToDelete(null)}
        onConfirm={handleDelete}
        title={`Delete "${groupToDelete?.name ?? ''}"?`}
        description="The group's users are not deleted — they return to the organization's policies. Alerts already triggered under this group keep naming it. This cannot be undone."
        confirmLabel="Delete"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
